import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, ShieldAlert, CheckCircle2, Copy, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirror server-side `assert_strong_password` so the URL cannot be
// burned by a weak password. Server still enforces this — the client
// check is UX polish.
function passwordProblems(pw) {
  const problems = [];
  if (!pw || pw.length < 12) problems.push('at least 12 characters');
  if (!/[A-Z]/.test(pw)) problems.push('an uppercase letter');
  if (!/[0-9]/.test(pw)) problems.push('a number');
  if (!/[^A-Za-z0-9]/.test(pw)) problems.push('a special character');
  return problems;
}

export default function AdminRescuePage() {
  const { key } = useParams();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [linkOk, setLinkOk] = useState(false);
  const [used, setUsed] = useState(false);

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false); // hard double-submit guard

  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/admin-rescue/${encodeURIComponent(key)}/status`);
        if (dead) return;
        if (data?.ok) {
          setLinkOk(true);
        } else {
          setLinkOk(false);
          if (data?.used) setUsed(true);
        }
      } catch (_e) {
        if (!dead) setLinkOk(false);
      } finally {
        if (!dead) setChecking(false);
      }
    })();
    return () => { dead = true; };
  }, [key]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    setError('');

    if (pw !== pwConfirm) {
      setError('Passwords do not match.');
      return;
    }
    const problems = passwordProblems(pw);
    if (problems.length) {
      setError(`Password must contain ${problems.join(', ')}.`);
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/admin-rescue`, {
        key,
        email: email.trim().toLowerCase(),
        totp_code: code.trim(),
        new_password: pw,
      });
      setResult({ email: data.email, recovery_key: data.recovery_key });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Reset failed. Please try again.';
      setError(detail);
      // ONLY treat a 410 from the server as "URL is now dead". A 400/401/404
      // is a user-input error — the URL is still valid to retry.
      if (status === 410) {
        setLinkOk(false);
        setUsed(true);
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(result.recovery_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) { /* clipboard blocked */ }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-100">
        <Loader2 className="w-6 h-6 animate-spin" data-testid="admin-rescue-checking" />
      </div>
    );
  }

  if (!linkOk) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-100 p-6">
        <Card className="w-full max-w-md bg-neutral-900 border-neutral-800" data-testid="admin-rescue-invalid">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-red-400" />
              <CardTitle>Link not available</CardTitle>
            </div>
            <CardDescription>
              {used
                ? 'This rescue link has been used and can no longer be reopened.'
                : 'This link does not exist.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/login')}
              data-testid="admin-rescue-back-to-login"
            >
              Back to login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-100 p-6">
        <Card className="w-full max-w-md bg-neutral-900 border-neutral-800" data-testid="admin-rescue-success">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              <CardTitle>Password reset</CardTitle>
            </div>
            <CardDescription>
              You can now log in to <span className="font-mono text-neutral-100">{result.email}</span> with your new password.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-neutral-300 mb-2 block">Your new recovery key</Label>
              <div className="flex items-center gap-2 rounded-md bg-neutral-950 border border-neutral-700 p-3">
                <KeyRound className="w-4 h-4 text-amber-400 shrink-0" />
                <code
                  className="flex-1 text-lg tracking-wider font-mono text-neutral-100 break-all"
                  data-testid="admin-rescue-key-value"
                >
                  {result.recovery_key}
                </code>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={copyKey}
                  data-testid="admin-rescue-key-copy"
                >
                  <Copy className="w-3.5 h-3.5 mr-1" />{copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="text-xs text-neutral-400 mt-2">
                Store this somewhere safe — it will not be shown again and this rescue URL is now permanently disabled.
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => navigate('/login')}
              data-testid="admin-rescue-go-login"
            >
              Continue to login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pwProblems = pw ? passwordProblems(pw) : [];

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-100 p-6">
      <Card className="w-full max-w-md bg-neutral-900 border-neutral-800 text-neutral-100" data-testid="admin-rescue-form-card">
        <CardHeader>
          <CardTitle className="text-neutral-100">Admin rescue</CardTitle>
          <CardDescription className="text-neutral-400">
            One-time password reset using your authenticator. Weak passwords, wrong codes,
            and typos will NOT burn this link — the URL is only consumed after your new
            password has been saved successfully.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="ar-email" className="text-neutral-200">Email</Label>
              <Input
                id="ar-email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@collectiveorb.com"
                className="bg-neutral-950 border-neutral-700 text-neutral-100"
                data-testid="admin-rescue-email-input"
              />
            </div>
            <div>
              <Label htmlFor="ar-code" className="text-neutral-200">6-digit authenticator code</Label>
              <Input
                id="ar-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="bg-neutral-950 border-neutral-700 text-neutral-100"
                data-testid="admin-rescue-code-input"
              />
            </div>
            <div>
              <Label htmlFor="ar-new" className="text-neutral-200">New password</Label>
              <Input
                id="ar-new"
                type="password"
                required
                autoComplete="new-password"
                minLength={12}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="bg-neutral-950 border-neutral-700 text-neutral-100"
                data-testid="admin-rescue-new-password-input"
              />
              <p className={`text-xs mt-1 ${pwProblems.length ? 'text-amber-400' : 'text-emerald-400'}`}>
                {pw
                  ? (pwProblems.length
                      ? `Still needs: ${pwProblems.join(', ')}.`
                      : 'Meets all requirements.')
                  : 'Min 12 chars, must include an uppercase letter, a number, and a special character.'}
              </p>
            </div>
            <div>
              <Label htmlFor="ar-confirm" className="text-neutral-200">Confirm new password</Label>
              <Input
                id="ar-confirm"
                type="password"
                required
                autoComplete="new-password"
                minLength={12}
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                className="bg-neutral-950 border-neutral-700 text-neutral-100"
                data-testid="admin-rescue-confirm-password-input"
              />
            </div>
            {error && (
              <Alert variant="destructive" data-testid="admin-rescue-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="admin-rescue-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
